import type { IBudgetStatus } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Box, Button, Group, NumberInput, Paper, Stack, Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { useAuth } from "../../../../hooks/useAuth"
import type { useCompanyApi } from "../../../../hooks/useCompanyApi"
import { deleteBudgetLimit, fetchBudgets, upsertBudget } from "../../../../lib/api"
import { notifyError } from "../../../../lib/notify"
import { queryKeys } from "../../../../lib/query-keys"
import { SettingsReadOnlyNotice } from "../SettingsReadOnlyNotice"
import { BudgetTabView } from "./BudgetTabView"
import type { IBudgetEditState } from "./BudgetTabView/interfaces"

interface IProps {
  company: ReturnType<typeof useCompanyApi>
  settings: Record<string, string>
  savingSettings: boolean
  /** Owner/admin (or super-admin) may edit the €/h rate; a plain member gets read-only. */
  canManage?: boolean
  updateSetting: (key: string, value: string) => void
  save: (keys?: string[]) => void
}

const DEFAULT_HOURLY_RATE = 75

export function BudgetTab({
  company,
  settings,
  savingSettings,
  canManage = true,
  updateSetting,
  save,
}: IProps): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const [editState, setEditState] = useState<IBudgetEditState | null>(null)

  const { data: budgets, isLoading } = useQuery({
    queryKey: queryKeys.budgets(orgId),
    queryFn: () => (company ? fetchBudgets(company) : Promise.resolve([])),
    enabled: !!company,
    refetchInterval: 60_000,
  })

  const { mutate: saveBudget, isPending: saving } = useMutation({
    mutationFn: async (state: IBudgetEditState) => {
      if (!company) throw new Error("No company")
      await upsertBudget(company, state.adapter, state.monthly_limit_credits, state.alert_threshold_pct)
    },
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Budget saved`),
        message: i18n._(msg`Changes applied`),
        color: "success",
      })
      setEditState(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets(orgId) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: removeBudget, isPending: removing } = useMutation({
    mutationFn: async (adapter: string) => {
      if (!company) throw new Error("No company")
      await deleteBudgetLimit(company, adapter)
    },
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Budget removed`),
        message: i18n._(msg`Limit cleared`),
        color: "success",
      })
      setEditState(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets(orgId) })
    },
    onError: (err) => notifyError(err),
  })

  const openEdit = useCallback((b: IBudgetStatus): void => {
    setEditState({
      adapter: b.adapter,
      monthly_limit_credits: b.monthly_limit_credits,
      alert_threshold_pct: b.alert_threshold_pct,
    })
  }, [])

  const hourlyRate = Number.parseInt(settings.meeting_cost_hourly_rate_eur ?? "", 10)

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Text fw={600} mb={4}>
          <Trans>Meeting cost estimate</Trans>
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          <Trans>
            Blended hourly rate used to estimate each meeting's cost (attendees × duration × rate). Defaults to €75/h.
          </Trans>
        </Text>
        <Group align="end" gap="sm">
          <NumberInput
            label={<Trans>Hourly rate (€)</Trans>}
            min={0}
            step={5}
            w={160}
            disabled={!canManage}
            value={Number.isFinite(hourlyRate) ? hourlyRate : DEFAULT_HOURLY_RATE}
            onChange={(v) => updateSetting("meeting_cost_hourly_rate_eur", String(typeof v === "number" ? v : v || ""))}
          />
          {canManage && (
            <Button onClick={() => save(["meeting_cost_hourly_rate_eur"])} loading={savingSettings}>
              <Trans>Save</Trans>
            </Button>
          )}
        </Group>
        {!canManage && (
          <Box mt="sm">
            <SettingsReadOnlyNotice />
          </Box>
        )}
      </Paper>
      <BudgetTabView
        hasCompany={!!company}
        budgets={budgets ?? []}
        isLoading={isLoading}
        saving={saving}
        removing={removing}
        editState={editState}
        onOpenEdit={openEdit}
        onCloseEdit={() => setEditState(null)}
        onEditStateChange={setEditState}
        onSaveBudget={saveBudget}
        onRemoveBudget={removeBudget}
      />
    </Stack>
  )
}
